import { useState, useEffect } from "react";
import { ModalShell } from "../ModalShell";
import { createDownloadUrl } from "../../api/storage";
import {
  fetchOrgCoupons,
  createOrgCoupon,
  toggleOrgCoupon,
  updateAdminOrganisationLocation,
  updateAdminOrganisationOfficerPrice,
} from "../../api/admin";

export function OrganisationDetailsModal({
  org: initialOrg,
  onClose,
  onUpdated,
}) {
  const [org, setOrg] = useState(initialOrg);
  const [activeTab, setActiveTab] = useState("profile"); // 'profile' | 'officers' | 'branches' | 'audit' | 'coupons'
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [docError, setDocError] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  // Location editing state
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [savingLocation, setSavingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationSuccess, setLocationSuccess] = useState(null);

  // Additional Officer Price editing state (Admin only)
  const currentOfficerPrice =
    org?.additionalOfficerPrice ??
    (org?.additionalOfficerFeePaise != null
      ? Math.round(org.additionalOfficerFeePaise / 100)
      : 1200);
  const [isEditingOfficerPrice, setIsEditingOfficerPrice] = useState(false);
  const [officerPriceInput, setOfficerPriceInput] = useState(currentOfficerPrice);
  const [savingOfficerPrice, setSavingOfficerPrice] = useState(false);
  const [officerPriceError, setOfficerPriceError] = useState(null);
  const [officerPriceSuccess, setOfficerPriceSuccess] = useState(null);

  useEffect(() => {
    setOrg(initialOrg);
    const p =
      initialOrg?.additionalOfficerPrice ??
      (initialOrg?.additionalOfficerFeePaise != null
        ? Math.round(initialOrg.additionalOfficerFeePaise / 100)
        : 1200);
    setOfficerPriceInput(p);
  }, [initialOrg]);

  // Coupons state (Admin-only for Professional verification fee discounts)
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponError, setCouponError] = useState(null);
  const [couponSuccess, setCouponSuccess] = useState(null);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [newCouponCode, setNewCouponCode] = useState("");
  const [newDiscountPercent, setNewDiscountPercent] = useState(50);
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [generatingCoupon, setGeneratingCoupon] = useState(false);
  const [togglingCouponId, setTogglingCouponId] = useState(null);

  const handleOpenEditLocation = () => {
    setLocationError(null);
    setLocationSuccess(null);
    const existing = [org?.city, org?.state].filter(Boolean).join(", ");
    setLocationInput(existing);
    setIsEditingLocation(true);
  };

  const handleSaveLocation = async (e) => {
    if (e) e.preventDefault();
    setSavingLocation(true);
    setLocationError(null);
    try {
      const res = await updateAdminOrganisationLocation(org.id, locationInput);
      const updated = res?.data || res;
      setOrg((prev) => ({
        ...prev,
        city: updated.city,
        state: updated.state,
      }));
      onUpdated?.({
        ...org,
        city: updated.city,
        state: updated.state,
      });
      setIsEditingLocation(false);
      setLocationSuccess("Location updated successfully.");
      setTimeout(() => setLocationSuccess(null), 3000);
    } catch (err) {
      setLocationError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update location.",
      );
    } finally {
      setSavingLocation(false);
    }
  };

  const handleSaveOfficerPrice = async (e) => {
    if (e) e.preventDefault();
    const num = Math.round(Number(officerPriceInput));
    if (isNaN(num) || num < 0) {
      setOfficerPriceError("Price must be a valid non-negative number.");
      return;
    }
    setSavingOfficerPrice(true);
    setOfficerPriceError(null);
    try {
      const res = await updateAdminOrganisationOfficerPrice(org.id, num);
      const updated = res?.data || res;
      setOrg((prev) => ({
        ...prev,
        additionalOfficerFeePaise: updated.additionalOfficerFeePaise,
        additionalOfficerPrice: updated.additionalOfficerPrice,
      }));
      onUpdated?.({
        ...org,
        additionalOfficerFeePaise: updated.additionalOfficerFeePaise,
        additionalOfficerPrice: updated.additionalOfficerPrice,
      });
      setIsEditingOfficerPrice(false);
      setOfficerPriceSuccess(
        `Extra officer price set to ₹${num.toLocaleString("en-IN")}.`,
      );
      setTimeout(() => setOfficerPriceSuccess(null), 3500);
    } catch (err) {
      setOfficerPriceError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update extra officer price.",
      );
    } finally {
      setSavingOfficerPrice(false);
    }
  };

  const handleResetOfficerPrice = async () => {
    setSavingOfficerPrice(true);
    setOfficerPriceError(null);
    try {
      const res = await updateAdminOrganisationOfficerPrice(org.id, 1200);
      const updated = res?.data || res;
      setOfficerPriceInput(1200);
      setOrg((prev) => ({
        ...prev,
        additionalOfficerFeePaise: updated.additionalOfficerFeePaise,
        additionalOfficerPrice: updated.additionalOfficerPrice,
      }));
      onUpdated?.({
        ...org,
        additionalOfficerFeePaise: updated.additionalOfficerFeePaise,
        additionalOfficerPrice: updated.additionalOfficerPrice,
      });
      setIsEditingOfficerPrice(false);
      setOfficerPriceSuccess("Reset to standard ₹1,200 per extra officer.");
      setTimeout(() => setOfficerPriceSuccess(null), 3500);
    } catch (err) {
      setOfficerPriceError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to reset extra officer price.",
      );
    } finally {
      setSavingOfficerPrice(false);
    }
  };

  if (!org) return null;

  const loadCoupons = async () => {
    if (!org?.id) return;
    setLoadingCoupons(true);
    setCouponError(null);
    try {
      const res = await fetchOrgCoupons(org.id);
      setCoupons(
        Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [],
      );
    } catch (err) {
      setCouponError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load coupons.",
      );
    } finally {
      setLoadingCoupons(false);
    }
  };

  useEffect(() => {
    if (org?.id) {
      loadCoupons();
    }
  }, [org?.id]);

  const suggestCode = (percent = newDiscountPercent) => {
    const orgPrefix =
      (org?.name || "ORG")
        .replace(/[^a-zA-Z0-9]/g, "")
        .slice(0, 4)
        .toUpperCase() || "TAP";
    const rand = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${orgPrefix}${percent || 50}-${rand}`;
  };

  const handleOpenGenerate = () => {
    setCouponError(null);
    setCouponSuccess(null);
    if (!newCouponCode) {
      setNewCouponCode(suggestCode(newDiscountPercent));
    }
    setShowGenerateForm(true);
  };

  const handleGenerateCoupon = async (e) => {
    if (e) e.preventDefault();
    const cleanCode = (newCouponCode || "").trim().toUpperCase();
    if (!cleanCode || cleanCode.length < 3) {
      setCouponError(
        "Please enter a valid coupon code (at least 3 characters).",
      );
      return;
    }
    const pct = parseInt(newDiscountPercent, 10);
    if (isNaN(pct) || pct < 1 || pct > 100) {
      setCouponError("Discount percentage must be between 1% and 100%.");
      return;
    }
    const maxUses = Math.max(1, parseInt(newMaxUses, 10) || 1);

    setGeneratingCoupon(true);
    setCouponError(null);
    setCouponSuccess(null);
    try {
      const res = await createOrgCoupon(org.id, {
        code: cleanCode,
        discountPercent: pct,
        maxUses,
      });
      const created = res?.data || res;
      setCoupons((prev) => [created, ...(prev || [])]);
      setCouponSuccess(`Coupon code "${cleanCode}" generated successfully!`);
      setShowGenerateForm(false);
      setNewCouponCode("");
      setNewDiscountPercent(50);
      setNewMaxUses(1);
    } catch (err) {
      setCouponError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to generate coupon.",
      );
    } finally {
      setGeneratingCoupon(false);
    }
  };

  const handleToggleCoupon = async (couponId) => {
    setTogglingCouponId(couponId);
    setCouponError(null);
    setCouponSuccess(null);
    try {
      const res = await toggleOrgCoupon(org.id, couponId);
      const updated = res?.data || res;
      setCoupons((prev) =>
        prev.map((c) =>
          c.id === couponId ? { ...c, status: updated.status } : c,
        ),
      );
      setCouponSuccess(`Coupon status updated to ${updated.status}.`);
    } catch (err) {
      setCouponError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update coupon status.",
      );
    } finally {
      setTogglingCouponId(null);
    }
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleDownloadDoc = async (s3Key) => {
    if (!s3Key) return;
    setDownloadingDoc(true);
    setDocError(null);
    try {
      const res = await createDownloadUrl(s3Key);
      if (res?.downloadUrl) {
        window.open(res.downloadUrl, "_blank", "noopener,noreferrer");
      } else {
        throw new Error("Download URL could not be generated.");
      }
    } catch (err) {
      setDocError(err.message || "Failed to open document.");
    } finally {
      setDownloadingDoc(false);
    }
  };

  const officers =
    org.officers || (org.primaryOfficer ? [org.primaryOfficer] : []);
  const branches = org.branches || [];

  const statusBadge = () => {
    switch (org.status) {
      case "APPROVED":
        return {
          bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
          dot: "bg-emerald-500",
          label: "Approved & Active",
        };
      case "PENDING_REVIEW":
        return {
          bg: "bg-amber-50 text-amber-700 border-amber-200",
          dot: "bg-amber-500",
          label: "Pending Review",
        };
      case "REJECTED":
        return {
          bg: "bg-red-50 text-red-700 border-red-200",
          dot: "bg-red-500",
          label: "Rejected",
        };
      case "SUSPENDED":
        return {
          bg: "bg-slate-100 text-slate-700 border-slate-300",
          dot: "bg-slate-500",
          label: "Suspended",
        };
      default:
        return {
          bg: "bg-slate-50 text-slate-600 border-slate-200",
          dot: "bg-slate-400",
          label: org.status || "Unknown",
        };
    }
  };

  const badge = statusBadge();

  // Clean website URL
  const cleanWebsite =
    org.websiteUrl && org.websiteUrl !== "null" ? org.websiteUrl.trim() : null;
  const websiteHref = cleanWebsite
    ? cleanWebsite.startsWith("http")
      ? cleanWebsite
      : `https://${cleanWebsite}`
    : null;

  return (
    <ModalShell
      title={org.name}
      subtitle={`Registration / GSTIN: ${org.registrationNumber || "Not Available"}`}
      onClose={onClose}
      maxWidth="max-w-4xl"
      zIndex="z-[10020]"
      fixedHeight={true}
    >
      <div className="flex flex-col min-h-0 flex-1 h-full overflow-hidden bg-slate-50/50">
        {/* Top Summary Bar */}
        <div className="p-4 sm:p-5 bg-white border-b border-slate-100 shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#16294C] to-[#08833E] flex items-center justify-center text-white font-bold text-lg shadow-sm shrink-0">
                {org.name?.slice(0, 2)?.toUpperCase() || "OR"}
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-slate-900 text-base sm:text-lg">
                    {org.name}
                  </h3>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badge.bg}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                    {badge.label}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-1 flex-wrap">
                  <span>
                    ID:{" "}
                    <span className="font-mono text-slate-700 font-medium">
                      {org.id}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => handleCopy(org.id, "id")}
                    className="text-slate-400 hover:text-slate-700 transition-colors"
                    title="Copy Organisation ID"
                  >
                    {copiedField === "id" ? (
                      <span className="text-emerald-600 font-semibold text-[10px]">
                        Copied!
                      </span>
                    ) : (
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    )}
                  </button>
                  <span className="text-slate-300">•</span>
                  <span>
                    Registered:{" "}
                    <span className="text-slate-700">
                      {org.createdAt
                        ? new Date(org.createdAt).toLocaleDateString("en-IN", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })
                        : "-"}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* Quick Metrics */}
            <div className="flex items-center gap-2 sm:gap-2.5 self-stretch sm:self-auto overflow-x-auto pb-1 sm:pb-0 shrink-0">
              <div className="px-2.5 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center min-w-[84px] sm:min-w-[92px]">
                <div className="text-base font-bold text-[#16294C]">
                  {org.branchesCount ?? branches.length ?? 0}
                </div>
                <div className="text-[10px] text-slate-500 uppercase font-medium tracking-tight whitespace-nowrap">
                  Branches
                </div>
              </div>
              <div className="px-2.5 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center min-w-[84px] sm:min-w-[92px]">
                <div className="text-base font-bold text-[#16294C]">
                  {org.officersCount ?? officers.length ?? 0}
                </div>
                <div className="text-[10px] text-slate-500 uppercase font-medium tracking-tight whitespace-nowrap">
                  Officers
                </div>
              </div>
              <div className="px-2 py-2 bg-slate-50 rounded-lg border border-slate-200 text-center min-w-[88px] sm:min-w-[96px]">
                <div className="text-base font-bold text-[#16294C]">
                  {org.verificationRequestsCount ?? 0}
                </div>
                <div className="text-[9.5px] sm:text-[10px] text-slate-500 uppercase font-medium tracking-tight whitespace-nowrap">
                  Verifications
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab("coupons")}
                className={`px-2.5 py-2 rounded-lg border text-center min-w-[84px] sm:min-w-[92px] transition cursor-pointer ${
                  activeTab === "coupons"
                    ? "bg-purple-50 border-purple-300 text-purple-900 shadow-xs"
                    : "bg-slate-50 border-slate-200 hover:bg-slate-100 text-[#16294C]"
                }`}
                title="Click to view and generate Verification Coupons"
              >
                <div className="text-base font-bold">
                  {coupons?.length ?? 0}
                </div>
                <div className="text-[10px] text-slate-500 uppercase font-medium tracking-tight whitespace-nowrap">
                  Coupons
                </div>
              </button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center gap-1 sm:gap-2 mt-4 pt-3 border-t border-slate-100 overflow-x-auto">
            <button
              type="button"
              onClick={() => setActiveTab("profile")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === "profile"
                  ? "bg-[#16294C] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Company Profile
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("officers")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === "officers"
                  ? "bg-[#16294C] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <span>Officers & Admins</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  activeTab === "officers"
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {org.officersCount ?? officers.length ?? 0}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("branches")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === "branches"
                  ? "bg-[#16294C] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <span>Branches</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  activeTab === "branches"
                    ? "bg-white/20 text-white"
                    : "bg-slate-200 text-slate-700"
                }`}
              >
                {org.branchesCount ?? branches.length ?? 0}
              </span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("audit")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === "audit"
                  ? "bg-[#16294C] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              Compliance & Audit
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("coupons")}
              className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                activeTab === "coupons"
                  ? "bg-[#16294C] text-white shadow-sm"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              }`}
            >
              <span>Verification Coupons</span>
              <span
                className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  activeTab === "coupons"
                    ? "bg-white/20 text-white"
                    : "bg-purple-100 text-purple-800"
                }`}
              >
                {coupons?.length ?? 0}
              </span>
            </button>
          </div>
        </div>

        {/* Modal Scrollable Content */}
        <div className="p-4 sm:p-6 overflow-y-auto min-h-0 flex-1 space-y-5">
          {docError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-xs sm:text-sm font-semibold flex items-center justify-between">
              <span>{docError}</span>
              <button
                onClick={() => setDocError(null)}
                className="text-red-700 hover:text-red-900 font-bold ml-2 text-xs"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* TAB 1: COMPANY PROFILE */}
          {activeTab === "profile" && (
            <div className="space-y-4">
              {/* Corporate Identity & Contact */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-500">
                    Corporate Identity & Contact
                  </h4>
                  {cleanWebsite && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-semibold text-[#08833E] hover:underline flex items-center gap-1"
                    >
                      <span>Visit Website</span>
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-slate-400 block text-xs">
                      Legal Business Name
                    </span>
                    <span className="font-semibold text-slate-800">
                      {org.name}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Registration / GSTIN
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="font-mono font-semibold text-slate-800">
                        {org.registrationNumber || "Not Provided"}
                      </span>
                      {org.registrationNumber && (
                        <button
                          type="button"
                          onClick={() =>
                            handleCopy(org.registrationNumber, "reg")
                          }
                          className="text-slate-400 hover:text-slate-700"
                          title="Copy GSTIN"
                        >
                          {copiedField === "reg" ? (
                            <span className="text-emerald-600 font-semibold text-[10px]">
                              Copied
                            </span>
                          ) : (
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Official Contact Email
                    </span>
                    {org.contactEmail ? (
                      <a
                        href={`mailto:${org.contactEmail}`}
                        className="font-medium text-blue-600 hover:underline"
                      >
                        {org.contactEmail}
                      </a>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Official Phone
                    </span>
                    {org.contactPhone ? (
                      <a
                        href={`tel:${org.contactPhone}`}
                        className="font-medium text-slate-800 hover:text-blue-600"
                      >
                        {org.contactPhone}
                      </a>
                    ) : (
                      <span className="text-slate-500">-</span>
                    )}
                  </div>

                  {/* Location with inline + button and editor */}
                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-400 block text-xs">
                        Location
                      </span>
                      {!isEditingLocation && (
                        <button
                          type="button"
                          onClick={handleOpenEditLocation}
                          className="w-4 h-4 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border border-slate-300 hover:border-blue-300"
                          title="Add / Update location"
                        >
                          +
                        </button>
                      )}
                    </div>

                    {isEditingLocation ? (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <input
                          type="text"
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          placeholder="City, State"
                          className="px-2 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-36 sm:w-44 bg-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveLocation(e);
                            if (e.key === "Escape") setIsEditingLocation(false);
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleSaveLocation}
                          disabled={savingLocation}
                          className="px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded shadow-2xs disabled:opacity-50 cursor-pointer"
                        >
                          {savingLocation ? "..." : "Update"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditingLocation(false)}
                          className="px-1.5 py-1 text-xs text-slate-500 hover:text-slate-700 cursor-pointer"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span className="font-semibold text-slate-800">
                        {[org.city, org.state].filter(Boolean).join(", ") ||
                          "Not configured"}
                      </span>
                    )}

                    {locationError && (
                      <p className="text-[11px] text-red-600 mt-1">
                        {locationError}
                      </p>
                    )}
                    {locationSuccess && (
                      <p className="text-[11px] text-emerald-600 font-medium mt-1">
                        {locationSuccess}
                      </p>
                    )}
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Verified Domain
                    </span>
                    <span className="font-medium text-slate-700">
                      {org.domain ? `@${org.domain}` : "Not configured"}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Routing Assignment Policy
                    </span>
                    <span className="font-semibold text-slate-800">
                      {org.assignmentPolicy === "EXPLICIT_ASSIGNMENT"
                        ? "Explicit Assignment"
                        : "Shared Pool (Default)"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Registered Address */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-3">
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 pb-2">
                  Registered Corporate Address
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-xs sm:text-sm">
                  <div className="sm:col-span-2">
                    <span className="text-slate-400 block text-xs">
                      Street Address
                    </span>
                    <span className="font-medium text-slate-800">
                      {org.addressLine1 || "Not specified"}
                      {org.addressLine2 ? `, ${org.addressLine2}` : ""}
                    </span>
                  </div>

                  <div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-400 block text-xs">
                        City & State
                      </span>
                      {!isEditingLocation && (
                        <button
                          type="button"
                          onClick={handleOpenEditLocation}
                          className="w-4 h-4 rounded-full bg-slate-100 hover:bg-blue-100 text-slate-600 hover:text-blue-700 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer border border-slate-300 hover:border-blue-300"
                          title="Add / Update location"
                        >
                          +
                        </button>
                      )}
                    </div>
                    {isEditingLocation ? (
                      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                        <input
                          type="text"
                          value={locationInput}
                          onChange={(e) => setLocationInput(e.target.value)}
                          placeholder="City, State"
                          className="px-2 py-1 text-xs border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-36 sm:w-44 bg-white"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveLocation(e);
                            if (e.key === "Escape") setIsEditingLocation(false);
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleSaveLocation}
                          disabled={savingLocation}
                          className="px-2.5 py-1 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded shadow-2xs disabled:opacity-50 cursor-pointer"
                        >
                          {savingLocation ? "..." : "Update"}
                        </button>
                        <button
                          type="button"
                          onClick={() => setIsEditingLocation(false)}
                          className="px-1.5 py-1 text-xs text-slate-500 hover:text-slate-700 cursor-pointer"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <span className="font-medium text-slate-800">
                        {org.city ? `${org.city}, ${org.state || ""}` : "-"}
                      </span>
                    )}
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Postal Pincode
                    </span>
                    <span className="font-mono font-medium text-slate-800">
                      {org.pincode || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Uploaded Certificate / Documentation */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                      Registration Certificate & Verification Documents
                    </h4>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Official document uploaded during company registration
                      (MCA, GST Certificate, PAN).
                    </p>
                  </div>

                  {org.registrationDocumentS3Key ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleDownloadDoc(org.registrationDocumentS3Key)
                      }
                      disabled={downloadingDoc}
                      className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#08833E] hover:bg-[#077035] text-white shadow-sm flex items-center gap-1.5 transition-colors cursor-pointer shrink-0 disabled:opacity-50"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {downloadingDoc ? "Opening..." : "View Document"}
                    </button>
                  ) : (
                    <span className="text-xs text-slate-400 italic bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 self-start sm:self-auto">
                      No document file on record
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: OFFICERS & ADMINS */}
          {activeTab === "officers" && (
            <div className="space-y-4">
              {/* Primary Admin Officer Card */}
              {org.primaryOfficer ? (
                <div className="bg-gradient-to-r from-slate-900 to-[#16294C] text-white rounded-xl p-4 sm:p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-wider font-bold text-emerald-400 bg-emerald-950/60 px-2.5 py-0.5 rounded border border-emerald-500/30">
                      Primary Administrator & Signatory
                    </span>
                    <span className="text-xs text-slate-300">
                      Employee ID:{" "}
                      <span className="font-mono font-bold text-white">
                        {org.primaryOfficer.employeeId || "-"}
                      </span>
                    </span>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                    <div>
                      <div className="text-sm sm:text-base font-bold text-white">
                        {org.primaryOfficer.fullName}
                      </div>
                      <div className="text-xs text-slate-300">
                        {org.primaryOfficer.designation ||
                          "HR Lead / Administrator"}
                      </div>
                    </div>

                    <div>
                      <div className="text-xs text-slate-300">
                        Email Address
                      </div>
                      <a
                        href={`mailto:${org.primaryOfficer.email || org.contactEmail}`}
                        className="text-xs font-semibold text-emerald-300 hover:underline break-all"
                      >
                        {org.primaryOfficer.email || org.contactEmail}
                      </a>
                    </div>

                    <div>
                      <div className="text-xs text-slate-300">Phone Number</div>
                      <div className="text-xs font-medium text-white">
                        {org.primaryOfficer.phone || org.contactPhone || "-"}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Officer Quota & Extra Officer Pricing Control */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center font-bold text-sm">
                      ₹
                    </div>
                    <div>
                      <h4 className="text-xs sm:text-sm font-bold text-slate-800">
                        Officer Quota & Extra Officer Pricing
                      </h4>
                      <p className="text-[11px] text-slate-500">
                        2 officers are included by default. Additional officers require payment set by Platform Admin.
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200 self-start sm:self-auto">
                    Admin Controlled
                  </span>
                </div>

                {/* Quota & Pricing Stats Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-[10px] uppercase font-bold text-slate-500 block">
                      Fixed Free Quota
                    </span>
                    <span className="text-base font-bold text-slate-900 mt-0.5 block">
                      2 Officers
                    </span>
                    <span className="text-[10px] text-emerald-600 font-medium">
                      Included with registration
                    </span>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-[10px] uppercase font-bold text-slate-500 block">
                      Current Officers
                    </span>
                    <span className="text-base font-bold text-slate-900 mt-0.5 block">
                      {officers.length} Registered
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">
                      {officers.length > 2
                        ? `${officers.length - 2} beyond free limit`
                        : `${2 - officers.length} free slot(s) remaining`}
                    </span>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <span className="text-[10px] uppercase font-bold text-slate-500 block">
                      Paid Extra Slots
                    </span>
                    <span className="text-base font-bold text-[#16294C] mt-0.5 block">
                      {org.paidVerificationOfficerSlots || 0} Slots
                    </span>
                    <span className="text-[10px] text-slate-500 font-medium">
                      Purchased & active
                    </span>
                  </div>

                  <div className="p-3 bg-indigo-50/60 rounded-lg border border-indigo-100 sm:col-span-1 col-span-2">
                    <span className="text-[10px] uppercase font-bold text-indigo-900 block">
                      Extra Officer Fee
                    </span>
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="text-base font-bold text-indigo-950">
                        ₹{currentOfficerPrice.toLocaleString("en-IN")}
                      </span>
                      <span className="text-[10px] text-indigo-600 font-medium">
                        / officer
                      </span>
                    </div>
                    <span className="text-[10px] text-indigo-700/80 font-medium">
                      {currentOfficerPrice === 1200
                        ? "Standard default price"
                        : "Custom price set by Admin"}
                    </span>
                  </div>
                </div>

                {/* Pricing Edit Form */}
                <div className="bg-slate-50/80 rounded-lg border border-slate-200 p-3 sm:p-3.5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="text-xs text-slate-700">
                      <span className="font-semibold text-slate-900">
                        Set Custom Extra Officer Price:
                      </span>{" "}
                      <span className="text-slate-500 text-[11px]">
                        This organisation will be charged this exact amount when adding an extra officer.
                      </span>
                    </div>

                    {!isEditingOfficerPrice ? (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setOfficerPriceInput(currentOfficerPrice);
                            setIsEditingOfficerPrice(true);
                            setOfficerPriceError(null);
                            setOfficerPriceSuccess(null);
                          }}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#16294C] hover:bg-[#0f1d36] text-white shadow-2xs transition-colors cursor-pointer"
                        >
                          Change Price
                        </button>
                        {currentOfficerPrice !== 1200 && (
                          <button
                            type="button"
                            onClick={handleResetOfficerPrice}
                            disabled={savingOfficerPrice}
                            className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 border border-slate-300 transition-colors cursor-pointer disabled:opacity-50"
                            title="Reset to default ₹1,200"
                          >
                            Reset to ₹1,200
                          </button>
                        )}
                      </div>
                    ) : (
                      <form
                        onSubmit={handleSaveOfficerPrice}
                        className="flex items-center gap-2 flex-wrap"
                      >
                        <div className="relative flex items-center">
                          <span className="absolute left-2.5 text-xs font-bold text-slate-500">
                            ₹
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="50"
                            value={officerPriceInput}
                            onChange={(e) =>
                              setOfficerPriceInput(e.target.value)
                            }
                            disabled={savingOfficerPrice}
                            placeholder="1200"
                            className="w-28 pl-6 pr-2.5 py-1 text-xs font-semibold bg-white border border-slate-300 rounded-lg text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={savingOfficerPrice}
                          className="px-3 py-1 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          {savingOfficerPrice ? "Saving..." : "Save Amount"}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingOfficerPrice(false);
                            setOfficerPriceInput(currentOfficerPrice);
                            setOfficerPriceError(null);
                          }}
                          disabled={savingOfficerPrice}
                          className="px-2.5 py-1 text-xs font-medium rounded-lg text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 cursor-pointer"
                        >
                          Cancel
                        </button>
                      </form>
                    )}
                  </div>

                  {officerPriceSuccess && (
                    <div className="mt-2.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-1.5 flex items-center gap-1.5">
                      <span>✓</span>
                      <span>{officerPriceSuccess}</span>
                    </div>
                  )}

                  {officerPriceError && (
                    <div className="mt-2.5 text-xs font-semibold text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-1.5 flex items-center gap-1.5">
                      <span>⚠</span>
                      <span>{officerPriceError}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* All Registered Officers Table */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-800">
                    All Authorized Officers ({officers.length})
                  </h4>
                  <span className="text-xs text-slate-500">
                    Officers with access to verify credentials
                  </span>
                </div>

                {officers.length === 0 ? (
                  <div className="py-8 text-center text-slate-500 text-xs">
                    No officer records found.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs text-slate-700">
                      <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase text-[10px] tracking-wider">
                        <tr>
                          <th className="px-4 py-3">Officer Name</th>
                          <th className="px-4 py-3">Role</th>
                          <th className="px-4 py-3">Designation</th>
                          <th className="px-4 py-3">Employee ID</th>
                          <th className="px-4 py-3">Email & Phone</th>
                          <th className="px-4 py-3 text-right">
                            Verifications
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {officers.map((officer) => (
                          <tr
                            key={officer.id || officer.employeeId}
                            className="hover:bg-slate-50/60"
                          >
                            <td className="px-4 py-3 font-semibold text-slate-900">
                              {officer.fullName}
                            </td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                  officer.role === "PRIMARY_ADMIN"
                                    ? "bg-purple-100 text-purple-800"
                                    : officer.role === "ADMIN"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-slate-100 text-slate-700"
                                }`}
                              >
                                {officer.role
                                  ? officer.role.replace(/_/g, " ")
                                  : "Officer"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {officer.designation || "-"}
                            </td>
                            <td className="px-4 py-3 font-mono text-slate-700">
                              {officer.employeeId || "-"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-slate-800">
                                {officer.email || "-"}
                              </div>
                              <div className="text-slate-400 text-[11px]">
                                {officer.phone || "-"}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-emerald-700 font-semibold">
                                {officer.verificationsApproved ?? 0}
                              </span>{" "}
                              <span className="text-slate-400">/</span>{" "}
                              <span className="text-red-600 font-semibold">
                                {officer.verificationsRejected ?? 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: BRANCHES */}
          {activeTab === "branches" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-xs sm:text-sm font-bold text-slate-800">
                  Regional & Operating Branches ({branches.length})
                </h4>
                <span className="text-xs text-slate-500">
                  Corporate branches registered on the platform
                </span>
              </div>

              {branches.length === 0 ? (
                <div className="p-8 bg-white rounded-xl border border-dashed border-slate-200 text-center space-y-2">
                  <div className="w-10 h-10 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                      />
                    </svg>
                  </div>
                  <h5 className="font-bold text-slate-800 text-sm">
                    No Regional Branches Registered
                  </h5>
                  <p className="text-xs text-slate-500 max-w-sm mx-auto">
                    This organisation operates under a single headquarters
                    without separate sub-branch logins.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {branches.map((b) => (
                    <div
                      key={b.id || b.name}
                      className="p-4 bg-white rounded-xl border border-slate-200 hover:border-slate-300 transition-all shadow-sm space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h5 className="font-bold text-slate-900 text-sm">
                              {b.name}
                            </h5>
                            {b.isHeadOffice && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800">
                                Head Office
                              </span>
                            )}
                          </div>
                          {b.branchCode && (
                            <span className="font-mono text-xs text-slate-500">
                              Code: {b.branchCode}
                            </span>
                          )}
                        </div>

                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                            b.isActive !== false
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {b.isActive !== false ? "Active" : "Inactive"}
                        </span>
                      </div>

                      <div className="text-xs text-slate-600 space-y-1">
                        <div>
                          <span className="text-slate-400">Address: </span>
                          <span>
                            {b.addressLine1 || "Address not provided"}
                            {b.city ? `, ${b.city}` : ""}
                            {b.state ? `, ${b.state}` : ""}
                            {b.pincode ? ` - ${b.pincode}` : ""}
                          </span>
                        </div>
                        {b.email && (
                          <div>
                            <span className="text-slate-400">
                              Contact Email:{" "}
                            </span>
                            <span className="font-medium text-slate-800">
                              {b.email}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 4: COMPLIANCE & AUDIT */}
          {activeTab === "audit" && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-sm space-y-4">
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-500 border-b border-slate-100 pb-2">
                  Platform Lifecycle & Approval History
                </h4>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-xs sm:text-sm">
                  <div>
                    <span className="text-slate-400 block text-xs">
                      Current Lifecycle Status
                    </span>
                    <span className="font-bold text-slate-900">
                      {org.status}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Application Submitted On
                    </span>
                    <span className="font-medium text-slate-800">
                      {org.createdAt
                        ? new Date(org.createdAt).toLocaleString("en-IN")
                        : "-"}
                    </span>
                  </div>

                  <div>
                    <span className="text-slate-400 block text-xs">
                      Last Updated
                    </span>
                    <span className="font-medium text-slate-800">
                      {org.updatedAt
                        ? new Date(org.updatedAt).toLocaleString("en-IN")
                        : "-"}
                    </span>
                  </div>

                  {org.approvedAt && (
                    <div>
                      <span className="text-slate-400 block text-xs">
                        Approved On
                      </span>
                      <span className="font-medium text-emerald-700">
                        {new Date(org.approvedAt).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                  {org.approvedBy && (
                    <div>
                      <span className="text-slate-400 block text-xs">
                        Approved By
                      </span>
                      <span className="font-mono text-slate-800">
                        {org.approvedBy}
                      </span>
                    </div>
                  )}

                  {org.rejectedAt && (
                    <div>
                      <span className="text-slate-400 block text-xs">
                        Rejected On
                      </span>
                      <span className="font-medium text-red-600">
                        {new Date(org.rejectedAt).toLocaleString("en-IN")}
                      </span>
                    </div>
                  )}

                  {org.rejectedBy && (
                    <div>
                      <span className="text-slate-400 block text-xs">
                        Rejected By
                      </span>
                      <span className="font-mono text-slate-800">
                        {org.rejectedBy}
                      </span>
                    </div>
                  )}

                  {org.rejectionReason && (
                    <div className="sm:col-span-2 md:col-span-3 bg-red-50 p-3 rounded-lg border border-red-200">
                      <span className="text-red-700 block text-xs font-bold">
                        Rejection Reason
                      </span>
                      <p className="text-xs text-red-900 mt-1 font-medium">
                        {org.rejectionReason}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Data Integrity Summary */}
              <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 text-xs text-slate-600 space-y-1.5">
                <div className="font-semibold text-slate-800">
                  Data Integrity & Platform Controls
                </div>
                <p>
                  This organisation is bound by TickAndPass platform terms. All
                  officer actions and credential verification decisions are
                  logged with tamper-evident audit trails.
                </p>
              </div>
            </div>
          )}

          {/* Coupons Tab Content (Admin Only) */}
          {activeTab === "coupons" && (
            <div className="space-y-5 animate-in fade-in duration-200">
              {/* Alert Feedback Messages */}
              {couponSuccess && (
                <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-xs sm:text-sm font-medium flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-emerald-600 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{couponSuccess}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCouponSuccess(null)}
                    className="text-emerald-700 hover:text-emerald-900 font-bold ml-2 text-xs cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {couponError && (
                <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-800 text-xs sm:text-sm font-medium flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4 text-red-600 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{couponError}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCouponError(null)}
                    className="text-red-700 hover:text-red-900 font-bold ml-2 text-xs cursor-pointer"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Coupons Header Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-5 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm sm:text-base font-bold text-slate-900">
                        Verification Fee Discount Coupons
                      </h3>
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Admin Only
                      </span>
                    </div>
                    {/* <p className="text-xs text-slate-500 mt-1 max-w-2xl leading-relaxed">
                      Generate discount coupons for <strong className="text-slate-700">{org.name}</strong>. Professionals applying for credential verification with this organisation can use these codes during verification fee checkout (standard ₹500 fee) to receive this discount.
                    </p> */}
                  </div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={loadCoupons}
                      disabled={loadingCoupons}
                      className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors cursor-pointer flex items-center gap-1.5"
                      title="Refresh coupon list"
                    >
                      <svg
                        className={`w-3.5 h-3.5 ${loadingCoupons ? "animate-spin" : ""}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      <span>Refresh</span>
                    </button>
                    {!showGenerateForm && (
                      <button
                        type="button"
                        onClick={handleOpenGenerate}
                        className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#16294C] hover:bg-[#0f1d36] text-white shadow-sm transition-all flex items-center gap-1.5 cursor-pointer"
                      >
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2.5"
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        <span>Generate Coupon</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Inline Generation Form */}
                {showGenerateForm && (
                  <form
                    onSubmit={handleGenerateCoupon}
                    className="mt-4 pt-4 border-t border-slate-200 bg-slate-50/80 -mx-4 -mb-4 sm:-mx-5 sm:-mb-5 p-4 sm:p-5 rounded-b-xl space-y-4"
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
                        <span>New Discount Coupon Generator</span>
                        <span className="text-[10px] font-normal text-slate-500 lowercase">
                          (manually set code & discount percentage)
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowGenerateForm(false)}
                        className="text-slate-400 hover:text-slate-600 text-xs font-semibold cursor-pointer"
                      >
                        Close Form
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-3.5">
                      {/* Coupon Code Input */}
                      <div className="sm:col-span-5">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Coupon Code <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="text"
                            required
                            placeholder="e.g. RELIANCE50"
                            value={newCouponCode}
                            onChange={(e) =>
                              setNewCouponCode(
                                e.target.value
                                  .toUpperCase()
                                  .replace(/[^A-Z0-9_-]/g, ""),
                              )
                            }
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-mono font-bold tracking-wider text-slate-900 uppercase focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setNewCouponCode(suggestCode(newDiscountPercent))
                            }
                            className="px-2.5 py-2 text-[11px] font-semibold rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 shrink-0 cursor-pointer"
                            title="Auto-suggest code"
                          >
                            Suggest
                          </button>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Manually enter code or click suggest. Uppercase
                          alphanumeric.
                        </p>
                      </div>

                      {/* Discount Percentage Input */}
                      <div className="sm:col-span-4">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Discount Percentage (%){" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            required
                            value={newDiscountPercent}
                            onChange={(e) => {
                              const val = parseInt(e.target.value, 10);
                              setNewDiscountPercent(
                                isNaN(val)
                                  ? ""
                                  : Math.min(100, Math.max(1, val)),
                              );
                            }}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                          />
                          <span className="text-xs font-bold text-slate-500">
                            %
                          </span>
                        </div>
                        {/* Quick Percentage Presets */}
                        <div className="flex items-center gap-1 mt-1.5">
                          {[10, 25, 50, 100].map((pct) => (
                            <button
                              key={pct}
                              type="button"
                              onClick={() => setNewDiscountPercent(pct)}
                              className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors cursor-pointer ${
                                newDiscountPercent === pct
                                  ? "bg-[#16294C] text-white"
                                  : "bg-slate-200/80 text-slate-700 hover:bg-slate-300"
                              }`}
                            >
                              {pct === 100 ? "100% (FREE)" : `${pct}%`}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Max Uses Input */}
                      <div className="sm:col-span-3">
                        <label className="block text-xs font-semibold text-slate-700 mb-1">
                          Max Uses
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10000"
                          value={newMaxUses}
                          onChange={(e) =>
                            setNewMaxUses(
                              Math.max(1, parseInt(e.target.value, 10) || 1),
                            )
                          }
                          className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#16294C] focus:border-transparent"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                          Redemptions allowed (default: 1).
                        </p>
                      </div>
                    </div>

                    {/* Calculation Summary Preview */}
                    <div className="p-3 bg-white rounded-lg border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs">
                      <div className="text-slate-600">
                        <span className="font-semibold text-slate-800">
                          Fee Impact Preview:
                        </span>{" "}
                        Standard Verification Fee:{" "}
                        <span className="line-through text-slate-400">
                          ₹500
                        </span>{" "}
                        <span className="text-slate-400">→</span> Discount:{" "}
                        <span className="text-emerald-700 font-semibold">
                          -₹
                          {Math.round(
                            (500 * (Number(newDiscountPercent) || 0)) / 100,
                          )}{" "}
                          ({newDiscountPercent || 0}%)
                        </span>{" "}
                        <span className="text-slate-400">→</span> Professional
                        Pays:{" "}
                        <strong className="text-slate-900 font-bold text-sm">
                          ₹
                          {Math.max(
                            0,
                            500 -
                              Math.round(
                                (500 * (Number(newDiscountPercent) || 0)) / 100,
                              ),
                          )}
                        </strong>
                      </div>
                      <div className="flex items-center gap-2 self-end sm:self-auto">
                        <button
                          type="button"
                          onClick={() => setShowGenerateForm(false)}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={generatingCoupon || !newCouponCode}
                          className="px-4 py-1.5 text-xs font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                        >
                          {generatingCoupon ? (
                            <>
                              <svg
                                className="w-3.5 h-3.5 animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                />
                              </svg>
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <svg
                                className="w-3.5 h-3.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2.5"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span>Save & Generate Coupon</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
              </div>

              {/* Coupons List Section */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                      Generated Coupons
                    </span>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-200 text-slate-700">
                      {coupons.length}
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-500">
                    Targeted for Professional verification fees
                  </span>
                </div>

                {loadingCoupons && coupons.length === 0 ? (
                  <div className="p-8 text-center text-xs text-slate-500">
                    <svg
                      className="w-6 h-6 animate-spin mx-auto text-slate-400 mb-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    <span>Loading organisation coupons...</span>
                  </div>
                ) : coupons.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3 text-slate-400">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.5"
                          d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                        />
                      </svg>
                    </div>
                    <div className="text-sm font-semibold text-slate-800">
                      No coupons generated yet
                    </div>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto mt-1">
                      Generate a verification fee discount coupon so
                      professionals can apply it during checkout.
                    </p>
                    <button
                      type="button"
                      onClick={handleOpenGenerate}
                      className="mt-4 px-4 py-2 text-xs font-semibold rounded-lg bg-[#16294C] hover:bg-[#0f1d36] text-white shadow-sm transition-all cursor-pointer inline-flex items-center gap-1.5"
                    >
                      <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2.5"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      <span>Generate First Coupon</span>
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50/50 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">
                          <th className="px-4 py-3">Coupon Code</th>
                          <th className="px-4 py-3">Discount</th>
                          <th className="px-4 py-3">Fee Payable</th>
                          <th className="px-4 py-3">Usage</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3">Created Date</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-medium">
                        {coupons.map((c) => {
                          const discountPct = c.discountPercent ?? 100;
                          const feePayable = Math.max(
                            0,
                            500 - Math.round((500 * discountPct) / 100),
                          );
                          const isUsed =
                            c.status === "USED" ||
                            (c.maxUses && c.usedCount >= c.maxUses);
                          const isDisabled = c.status === "DISABLED";

                          return (
                            <tr
                              key={c.id}
                              className="hover:bg-slate-50/80 transition-colors"
                            >
                              {/* Code */}
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-mono font-bold text-xs px-2.5 py-1 bg-slate-100 text-[#16294C] border border-slate-200 rounded-md tracking-wider">
                                    {c.code}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleCopy(c.code, `coupon_${c.id}`)
                                    }
                                    className="p-1 text-slate-400 hover:text-slate-700 transition-colors cursor-pointer"
                                    title="Copy coupon code"
                                  >
                                    {copiedField === `coupon_${c.id}` ? (
                                      <span className="text-emerald-600 font-semibold text-[10px]">
                                        Copied!
                                      </span>
                                    ) : (
                                      <svg
                                        className="w-3.5 h-3.5"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                        />
                                      </svg>
                                    )}
                                  </button>
                                </div>
                              </td>

                              {/* Discount */}
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                                    discountPct === 100
                                      ? "bg-purple-50 text-purple-700 border border-purple-200"
                                      : "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                  }`}
                                >
                                  {discountPct === 100
                                    ? "100% OFF (FREE)"
                                    : `${discountPct}% OFF`}
                                </span>
                              </td>

                              {/* Fee Payable */}
                              <td className="px-4 py-3">
                                <div className="text-slate-800 font-semibold">
                                  ₹{feePayable}
                                </div>
                                <div className="text-[10px] text-slate-400">
                                  normally ₹500
                                </div>
                              </td>

                              {/* Usage */}
                              <td className="px-4 py-3">
                                <div className="text-slate-700 font-semibold">
                                  {c.usedCount ?? 0} / {c.maxUses ?? 1} used
                                </div>
                                <div className="w-20 bg-slate-100 rounded-full h-1.5 mt-1 overflow-hidden">
                                  <div
                                    className={`h-full ${isUsed ? "bg-slate-400" : "bg-emerald-500"}`}
                                    style={{
                                      width: `${Math.min(100, ((c.usedCount ?? 0) / (c.maxUses || 1)) * 100)}%`,
                                    }}
                                  />
                                </div>
                              </td>

                              {/* Status */}
                              <td className="px-4 py-3">
                                {isUsed ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                                    Used
                                  </span>
                                ) : isDisabled ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                                    Disabled
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                    Available
                                  </span>
                                )}
                              </td>

                              {/* Created Date */}
                              <td className="px-4 py-3 text-slate-500 text-[11px]">
                                {c.createdAt
                                  ? new Date(c.createdAt).toLocaleDateString(
                                      "en-IN",
                                      {
                                        day: "numeric",
                                        month: "short",
                                        year: "numeric",
                                      },
                                    )
                                  : "-"}
                              </td>

                              {/* Actions */}
                              <td className="px-4 py-3 text-right">
                                {!isUsed && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleCoupon(c.id)}
                                    disabled={togglingCouponId === c.id}
                                    className={`px-2.5 py-1 rounded text-xs font-semibold border transition-colors cursor-pointer ${
                                      isDisabled
                                        ? "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                                        : "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"
                                    }`}
                                  >
                                    {togglingCouponId === c.id
                                      ? "Updating..."
                                      : isDisabled
                                        ? "Enable"
                                        : "Disable"}
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-4 py-3 sm:px-6 sm:py-4 bg-white border-t border-slate-200 flex items-center justify-between shrink-0">
          {/* <div className="text-xs text-slate-500">
            Click outside or press <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono text-[10px]">Esc</kbd> to return to list
          </div> */}

          <div className="flex items-center gap-2">
            {org.registrationDocumentS3Key && (
              <button
                type="button"
                onClick={() => handleDownloadDoc(org.registrationDocumentS3Key)}
                disabled={downloadingDoc}
                className="px-3.5 py-2 text-xs font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                {downloadingDoc ? "Opening..." : "Download Certificate"}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#16294C] hover:bg-[#0f1d36] text-white shadow-sm transition-colors cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
